import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSectionStyles } from './customerForm/styles';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  Badge,
  Button,
  Input,
  Link as FluentLink,
  Spinner,
  Switch,
  Textarea,
  Toast,
  ToastBody,
  ToastTitle,
  Toaster,
  Tooltip,
  useId,
  useToastController,
} from '@fluentui/react-components';
import {
  Mail20Regular,
  DocumentAdd20Regular,
  Receipt20Regular,
  Alert20Filled,
  Delete16Regular,
} from '@fluentui/react-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryClient';
import { fetchOrderLocalRow } from '../services/orderService';
import { fetchEmailTemplates } from '../services/emailTemplateService';
import { apiFetch } from '../lib/apiFetch';
import { formatBusinessDateTime } from '../lib/businessTime';
import SendDocumentDialog from './SendDocumentDialog';
import { DocumentDetails } from '../services/documentService';
import { fetchCustomerContacts } from '../services/customerService';
import DetailsSidebar, { type DetailsSidebarSection } from './DetailsSidebar';
import PaymentDialog from './PaymentDialog';
import PaymentRowHoverCard from './PaymentRowHoverCard';
import PaymentMethodIcon from './PaymentMethodIcon';
import { type TonnageOverageSummary } from './TonnageSection';
import PhotoSection from './PhotoSection';
import { buildReceiptLabels } from '../lib/tonnage';

const useStyles = makeStyles({
  sidebarButton: {
    justifyContent: 'center',
    // The action buttons live in stretch-column flex containers. Cap them at
    // 160px with centered labels so they read as compact pills rather than
    // full-width bars that grow with the resizable rail.
    maxWidth: '160px',
  },
  // Orange notification glyph beside the Payments heading — replaces the old
  // full-width "Payment Due" banner. Same orange as the banner it supersedes.
  paymentDueIcon: {
    color: '#F7630C',
    flexShrink: 0,
  },
  // The tag input shares a flex-wrap row with the tag chips. Without an
  // explicit shrink/min-width the Fluent Input keeps its intrinsic width and
  // pushes past the 260px sidebar edge — constrain it so it wraps to a new
  // line and fills the remaining width instead of overflowing.
  tagInput: {
    flexGrow: 1,
    flexBasis: '120px',
    minWidth: 0,
    maxWidth: '100%',
  },
  // Mirrors the empty-state styling DetailsSidebar uses for sections with no
  // body — repeated here for the case where a body is provided but renders
  // an "empty" branch (e.g. Invoices in loading-finished + zero-result state).
  emptyBody: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  invoiceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    paddingTop: '4px',
    paddingBottom: '4px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    ...shorthands.border('0'),
    width: '100%',
    textAlign: 'left',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  invoiceIconWrap: {
    width: '28px',
    height: '28px',
    flexShrink: 0,
    ...shorthands.borderRadius('50%'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPalettePumpkinBorderActive),
    color: tokens.colorPalettePumpkinBorderActive,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  invoiceLabel: {
    flex: 1,
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
  },
  invoiceAmount: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  invoiceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  // Payments section: pill at top, aggregate Paid/Due rows, then a
  // chronological list of individual payment rows (date · method · amount).
  paymentsTotalsBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  paymentsTotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  paymentsTotalDueRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontWeight: 700,
  },
  paymentsTotalLabelMuted: {
    color: tokens.colorNeutralForeground2,
  },
  // Divider separates aggregate rows from the per-payment list so the
  // two blocks read as distinct sections within the accordion body.
  paymentsDivider: {
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.margin('4px', '0', '0', '0'),
  },
  paymentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  paymentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingTop: '4px',
    paddingBottom: '4px',
    fontSize: '13px',
  },
  paymentDate: {
    color: tokens.colorNeutralForeground2,
    minWidth: '80px',
    flexShrink: 0,
  },
  paymentMethod: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: tokens.colorNeutralForeground1,
    flex: 1,
    minWidth: 0,
    // Truncate long method labels (e.g. backend-encoded `Method: cash —
    // Date: 2026-05-21`) so the amount column stays visible.
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  paymentMethodIcon: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  paymentAmount: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    flexShrink: 0,
  },
  // Voided payments collected no money — strike the amount and mute it so the
  // row reads as reversed at a glance (B4-min).
  paymentAmountVoided: {
    textDecorationLine: 'line-through',
    color: tokens.colorNeutralForeground3,
  },
  paymentsEmpty: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  // Placement section (#8): read-only record of the customer's booking-flow
  // submission. A stack of labeled blocks — small muted label over the value.
  placementBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  placementField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  placementLabel: {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: tokens.colorNeutralForeground3,
  },
  placementValue: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
  },
  placementAck: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
  },
  // The acknowledgment is gated by the website checkout, so it's always true
  // for site orders — render it as a quiet, muted audit footnote rather than a
  // status field competing for attention.
  placementAuditNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  placementAuditIcon: {
    color: tokens.colorNeutralForeground4,
    flexShrink: 0,
  },
  // Editable placement (admin/phone-in orders): stretch the textareas to the
  // panel width and keep the acknowledgment label legible.
  placementTextarea: {
    width: '100%',
  },
  placementCheckboxLabel: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
  },
  // Checkbox + "Script" button share a row; wraps to a second line when the
  // panel is narrow so neither gets clipped.
  placementAckRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
  },
  // Phone-script dialog: muted instruction above a callout block holding the
  // lines the agent reads aloud, then the acknowledgment checkbox.
  scriptIntro: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    marginTop: 0,
    marginBottom: '12px',
  },
  scriptBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('12px', '14px'),
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  scriptList: {
    marginTop: 0,
    marginBottom: 0,
    paddingLeft: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  scriptDialogCheckbox: {
    marginTop: '16px',
  },
  // Warning chip surfaced on captured-but-unsynced rows. White fill with a
  // bright-orange (Pumpkin) border + text — the project's accent-not-fill
  // warning language.
  unsyncedChip: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorPalettePumpkinBorderActive,
    ...shorthands.border('1px', 'solid', tokens.colorPalettePumpkinBorderActive),
    ...shorthands.borderRadius('4px'),
    fontSize: '11px',
    fontWeight: 600,
    paddingTop: '2px',
    paddingBottom: '2px',
    paddingLeft: '6px',
    paddingRight: '6px',
    flexShrink: 0,
  },
  // In-flight ACH: informational, not a warning — an ACH debit settles over a
  // few business days, so this is an expected state, not a sync failure.
  pendingChip: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    fontSize: '11px',
    fontWeight: 600,
    paddingTop: '2px',
    paddingBottom: '2px',
    paddingLeft: '6px',
    paddingRight: '6px',
    flexShrink: 0,
  },
});

interface OrderDocument {
  id: string;
  document_type: string;
  number: string | null;
  finalized: boolean;
  grand_total_in_cents: number | null;
  to_be_paid_in_cents: number | null;
  paid_in_cents: number | null;
  payment_status: string | null;
}

// Individual payment row served by GET /booqable/orders/:id/payments.
// Backend flattens Booqable's `payments` resource attributes into this
// shape so the sidebar doesn't have to walk JSON:API relationships.
// `booqable_synced` is `false` when the row is a TWM-side captured txn
// whose follow-up Booqable create_payment never landed (resilience case
// — operator can one-click /admin/transactions/<id>/sync-booqable to
// retry). When true (or omitted), the row exists in Booqable normally.
interface OrderPayment {
  id: string;
  amount_in_cents: number | null;
  payment_method: string | null;
  mode: string | null;
  description: string | null;
  status: string | null;
  paid_at: string | null;
  booqable_synced?: boolean;
  processor_transaction_id?: string | null;
  // TWM PaymentTransaction UUID for the refund action — present on settled
  // card rows (incl. Booqable-sourced rows matched by AN txn id). Falls back
  // to `id` when absent.
  transaction_id?: string | null;
  // Cents already refunded against this charge (sum of its refund rows).
  amount_refunded_in_cents?: number | null;
  // Technology fee (cents) charged on top of the base for card payments.
  // amount_in_cents is the order-facing base; the card was charged base + fee.
  fee_in_cents?: number | null;
  // True when a captured card charge is still unsettled at AN and so can be
  // voided (free) — backend derives it from AN's live settlement state.
  voidable?: boolean;
}

const formatMoney = (cents: number | null | undefined) => {
  const value = ((cents ?? 0) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `$${value}`;
};

// Date in DD-MM-YYYY to match the reference UI (e.g. "21-05-2026"). The
// payments endpoint returns ISO timestamps; this strips the time and
// reorders.
const formatPaymentDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

// Map raw payment_method/mode strings from Booqable into a short staff-
// facing label + icon. Booqable's manual-mode rows arrive with method
// either set on the attribute or encoded in `description` (e.g.
// "Method: cash — Date: 2026-05-21") because the v4 API doesn't expose
// the sub-method as a structured field — see /booqable/orders/<id>/payments
// POST handler in backend/app.py.
const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  check: 'Check',
  bank_transfer: 'Bank transfer',
  ach: 'ACH',
  card: 'Card',
  credit_card: 'Card',
  manual: 'Manual',
  other: 'Other',
};

const resolvePaymentMethod = (p: OrderPayment): string => {
  const raw = (p.payment_method || '').trim().toLowerCase();
  if (raw && PAYMENT_METHOD_LABELS[raw]) return PAYMENT_METHOD_LABELS[raw];
  if (raw) return raw.charAt(0).toUpperCase() + raw.slice(1);
  const desc = (p.description || '').trim();
  // Auth.Net flow writes description as `Authorize.net (<type>) txn=<id>`
  // (see /booqable/orders/<id>/payments POST handler in backend/app.py).
  const anMatch = desc.match(/authorize\.net\s*\(([a-zA-Z_\- ]+)\)/i);
  if (anMatch) {
    const m = anMatch[1].trim().toLowerCase();
    return PAYMENT_METHOD_LABELS[m] || (m.charAt(0).toUpperCase() + m.slice(1));
  }
  // Backend encodes manual method in `description` as "Method: <name> — ..."
  const match = desc.match(/method:\s*([a-zA-Z_\- ]+)/i);
  if (match) {
    const m = match[1].trim().toLowerCase();
    return PAYMENT_METHOD_LABELS[m] || (m.charAt(0).toUpperCase() + m.slice(1));
  }
  if ((p.mode || '').toLowerCase() === 'manual') return 'Manual';
  return 'Payment';
};

interface OrderRightSidebarProps {
  /** Booqable order ID (or TWM UUID). When set, the Invoices section lists
   *  the order's documents; otherwise it shows the empty placeholder. */
  orderId?: string | null;
  /** TWM order UUID — present for any *saved* order, promoted or not (unlike
   *  `orderId` which the page nulls until promotion). Tonnage + attachments
   *  persist against the local order row, so the section keys off this. */
  orderLocalId?: string | null;
  /** True once the order has been promoted to a real Booqable order. Tags +
   *  Notes stay disabled until then — there's nothing to persist against
   *  while the order is still a pre-promotion draft. */
  isPromoted?: boolean;
  /** Human-readable order number for the breadcrumb on the document page. */
  orderNumber?: string | number | null;
  /** TWM customer UUID — used to look up saved cards (Auth.Net CIM) so the
   *  Payment dialog can show one-click charge chips. */
  customerId?: string | null;
  /** Customer email + name — used to pre-fill the Send email dialog. */
  customerEmail?: string | null;
  customerName?: string | null;
  /** Billing address fields from the order's billing dialog state. Mapped to
   *  Auth.Net's `billTo` on the charge request so AVS can run — without this
   *  the processor returns reason code 35 ("Call Merchant Service Provider")
   *  on every CNP card transaction for established merchant accounts. */
  billingAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    region?: string;
    postalCode?: string;
    country?: string;
  } | null;
  /** Customer phone — sent to AN's billTo.phoneNumber on card charge for
   *  fraud verification + chargeback defense. */
  customerPhone?: string | null;
  /** Order line items — mapped to AN's lineItems on card charge. Provides
   *  itemized transaction context that strengthens chargeback defense and
   *  populates the customer's bank statement detail. */
  orderLineItems?: Array<{
    id: string;
    name: string;
    quantity: number;
    priceInCents: number;
  }> | null;
  /** Order tax in cents — populates AN's tax.amount on card charge. */
  orderTaxCents?: number | null;
  /** Order-level payment status (paid / partially_paid / payment_due /
   *  overpaid). Renders as a pill at the top of the Payments section. */
  paymentStatus?: string | null;
  /** Mobile overlay state. When true the panel is slid off-screen; the
   *  header toggle on OrderDetails drives it. Desktop ignores this — the
   *  panel always sits inline. */
  collapsed?: boolean;
  /** Invoked when the user dismisses the mobile overlay via its backdrop.
   *  OrderDetails responds by setting `collapsed` back to true. */
  onClose?: () => void;
  /** True when this order is a phase of a project — gates the project-override
   *  toggle pinned at the bottom of the panel. */
  isPhase?: boolean;
  /** Current state of the project-override toggle. Owned by OrderDetails (it
   *  drives the per-section read-only locks); the sidebar only renders + flips it. */
  allowProjectOverride?: boolean;
  /** Flips the project-override toggle. */
  onAllowProjectOverrideChange?: (checked: boolean) => void;
}

interface SavedCardEntry {
  id: string;
  brand: string;
  last4: string;
}

// Tonnage receipt attachment row (subset of the attachments serializer) — drives
// the per-receipt entries offered in the Send-Email dialog.
interface ReceiptAttachmentRow {
  id: string;
  name: string;
  line_id: string | null;
  line_label: string | null;
  created_at: string | null;
}

// Tags Booqable uses internally for shared-tenant env classification — staff
// never manage these. Filtered out of the editable chip set; the backend
// re-applies them on save so a UI overwrite can't strip the env marker.
const ORDER_SYSTEM_TAGS = new Set(['SANDBOX']);

const OrderRightSidebar: React.FC<OrderRightSidebarProps> = ({
  orderId,
  orderLocalId,
  isPromoted,
  orderNumber,
  customerId,
  customerEmail,
  customerName,
  billingAddress,
  customerPhone,
  orderLineItems,
  orderTaxCents,
  paymentStatus,
  collapsed,
  onClose,
  isPhase,
  allowProjectOverride,
  onAllowProjectOverrideChange,
}) => {
  const styles = useStyles();
  const sectionStyles = useSectionStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Warm the Send-Email template cache when the order is known, so the send
  // dialog's default "Invoice send" body renders instantly on open instead of a
  // beat later (the server-side variable resolve is a ~2s multi-round-trip).
  // Same query key the dialog reads; mirrors the prefetch in DocumentEdit (#1174).
  React.useEffect(() => {
    if (!orderId) return;
    queryClient.prefetchQuery({
      queryKey: queryKeys.emailTemplates.list(orderId),
      queryFn: () => fetchEmailTemplates(orderId),
    });
  }, [orderId, queryClient]);

  const toasterId = useId('order-sidebar-toaster');
  const { dispatchToast } = useToastController(toasterId);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  // Order documents (invoices) — drives the Invoices section. Reads live from
  // Booqable via the backend so a freshly auto-created pro forma is visible
  // without waiting for the webhook→snapshot round trip. OrderDetails
  // invalidates this query key on line add/remove so the sidebar refetches.
  const orderDocumentsQuery = useQuery<{ data: OrderDocument[] }>({
    queryKey: ['order-documents', orderId],
    queryFn: async () => {
      const res = await apiFetch(`/booqable/orders/${encodeURIComponent(orderId!)}/documents`);
      if (!res.ok) throw new Error(`order-documents fetch failed: ${res.status}`);
      return res.json();
    },
    enabled: !!orderId,
    staleTime: 30 * 1000,
  });
  // ALL landfill (tonnage) receipt scans on the order, order-wide (no line
  // scope — the per-line galleries fetch their own line-scoped slices under
  // `[..., 'tonnage_receipt', <lineId>]`). A per-line upload invalidates the
  // whole `['order-attachments', orderLocalId]` family, so this list stays
  // fresh. Drives the per-receipt entries offered in the Send dialog — each is
  // attachable individually; the backend rejects a receipt id with no scan.
  const receiptAttachmentsQuery = useQuery<{ data: ReceiptAttachmentRow[] }>({
    queryKey: ['order-attachments', orderLocalId, 'tonnage_receipt', null],
    queryFn: async () => {
      const res = await apiFetch(
        `/orders/${encodeURIComponent(orderLocalId!)}/attachments?kind=tonnage_receipt`,
      );
      if (!res.ok) throw new Error(`attachments fetch failed: ${res.status}`);
      return res.json();
    },
    enabled: !!orderLocalId,
    staleTime: 60 * 1000,
  });
  const receiptAttachments = receiptAttachmentsQuery.data?.data ?? [];
  // Customer purchase-order scans on the order (kind='purchase_order'), uploaded
  // via the Photos & Attachments dialog. Shares the `['order-attachments',
  // orderLocalId]` invalidation family so a new PO upload refreshes this list.
  // Each becomes an individually-selectable attachment in the Send dialog.
  const purchaseOrderAttachmentsQuery = useQuery<{ data: ReceiptAttachmentRow[] }>({
    queryKey: ['order-attachments', orderLocalId, 'purchase_order', null],
    queryFn: async () => {
      const res = await apiFetch(
        `/orders/${encodeURIComponent(orderLocalId!)}/attachments?kind=purchase_order`,
      );
      if (!res.ok) throw new Error(`attachments fetch failed: ${res.status}`);
      return res.json();
    },
    enabled: !!orderLocalId,
    staleTime: 60 * 1000,
  });
  const purchaseOrderAttachments = purchaseOrderAttachmentsQuery.data?.data ?? [];
  const invoices = (orderDocumentsQuery.data?.data || []).filter(
    (d) => d.document_type === 'invoice'
  );
  // Outstanding balance across the order's invoices. Drives the "Due" row and
  // gates the New Payment button — no point opening the payment dialog when
  // there's nothing left to collect.
  const amountDueCents = invoices.reduce(
    (sum, inv) => sum + Math.max(0, (inv.grand_total_in_cents ?? 0) - (inv.paid_in_cents ?? 0)),
    0
  );

  // Saved cards (Auth.Net CIM) — drives the chips inside the Payment dialog's
  // Credit card step. Empty array when the customer has never opted into
  // save-on-file. Cheap call; backend short-circuits when authnet_customer_id
  // is null without touching Auth.Net. Prefetched as soon as the customer is
  // known (not gated on the dialog) so the Payment dialog opens without a
  // fetch stall; the 60s staleTime keeps repeat opens from refetching.
  const savedCardsQuery = useQuery<{ data: SavedCardEntry[] }>({
    queryKey: ['customer-saved-cards', customerId],
    queryFn: async () => {
      const res = await apiFetch(`/customers/${encodeURIComponent(customerId!)}/saved-cards`);
      if (!res.ok) throw new Error(`saved-cards fetch failed: ${res.status}`);
      return res.json();
    },
    enabled: !!customerId,
    staleTime: 60 * 1000,
  });

  // Individual payments for the order — drives the per-payment list in
  // the Payments accordion. Shares its cache key family with the order
  // documents + payment status so a New Payment dialog invalidation can
  // refresh all three at once.
  const orderPaymentsQuery = useQuery<{ data: OrderPayment[] }>({
    queryKey: ['order-payments', orderId],
    queryFn: async () => {
      const res = await apiFetch(`/booqable/orders/${encodeURIComponent(orderId!)}/payments`);
      if (!res.ok) throw new Error(`order-payments fetch failed: ${res.status}`);
      return res.json();
    },
    enabled: !!orderId,
    staleTime: 30 * 1000,
  });
  // Newest payments first. Booqable returns ascending by created_at; we
  // reverse so the most recent activity is at the top of the list.
  const payments = React.useMemo(() => {
    const list = orderPaymentsQuery.data?.data ?? [];
    return [...list].sort((a, b) => {
      const ta = a.paid_at ? Date.parse(a.paid_at) : 0;
      const tb = b.paid_at ? Date.parse(b.paid_at) : 0;
      return tb - ta;
    });
  }, [orderPaymentsQuery.data]);

  const openDocument = (id: string) => {
    navigate(`/documents/${id}`, {
      // Carry the local order id too, so the opened document's Send-Email dialog
      // can pull this order's landfill (tonnage) receipts into its attachment
      // list — same set the sidebar's own Send dialog offers.
      state: { fromOrder: { id: orderId, number: orderNumber, localId: orderLocalId } },
    });
  };

  // "New quote" — create a quote document for the order, then open its
  // preview exactly like clicking an invoice does (openDocument). Mirrors the
  // pro-forma-invoice flow: POST to the order's documents endpoint, refresh the
  // sidebar's document list, then navigate to the new doc.
  const [creatingQuote, setCreatingQuote] = useState(false);
  const handleNewQuote = async () => {
    if (!orderId || creatingQuote) return;
    setCreatingQuote(true);
    try {
      const res = await apiFetch(`/booqable/orders/${encodeURIComponent(orderId)}/documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_type: 'quote' }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Quote create failed (HTTP ${res.status})`);
      }
      const { id } = await res.json();
      if (!id) throw new Error('Quote create returned no document id');
      queryClient.invalidateQueries({ queryKey: ['order-documents', orderId] });
      openDocument(id);
    } catch (err: any) {
      dispatchToast(
        <Toast>
          <ToastTitle>Couldn't create quote</ToastTitle>
          <ToastBody>{err?.message || 'Please try again.'}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    } finally {
      setCreatingQuote(false);
    }
  };

  // Contacts linked to this customer. Only a Company has them (the endpoint
  // returns [] for individuals), so this is how we tell a company order apart
  // without threading customerKind down. Drives the Send-email recipient:
  // an order owned by a company should email the company's contact, not the
  // company record's own (often generic/empty) address.
  const contactsQuery = useQuery({
    queryKey: queryKeys.customers.contacts(customerId || ''),
    queryFn: () => fetchCustomerContacts(customerId!),
    enabled: !!customerId && !!orderId,
    staleTime: 60 * 1000,
  });
  // Contacts come back ordered by name; use the first one that has an email.
  // Fall back to the customer's own email when there's no contact (individual
  // order) or the contact has no email on file.
  const primaryContactEmail =
    (contactsQuery.data || []).find((c) => c.email)?.email || null;
  const effectiveRecipientEmail = primaryContactEmail || customerEmail || '';

  // Per-order tech-fee waiver toggle (persists to orders.waive_tech_fee via
  // PUT /orders/<localId>; honored by the pay link at click time). Fetched from
  // the order row, seeded once, then locally authoritative — optimistic w/ revert.
  // Declared above sendPayload because that memo seeds the Send dialog from it.
  const [waiveFee, setWaiveFee] = useState(false);
  // Shares the local-row cache (queryKeys.orders.localRow) with OrderDetails and
  // the customer-notes gate so the waive-fee flag rides the same GET /orders/:id
  // instead of a fourth one. `waive_tech_fee` is a field on the full row.
  const orderWaiveQuery = useQuery<{ data?: { waive_tech_fee?: boolean } }>({
    queryKey: queryKeys.orders.localRow(orderLocalId!),
    queryFn: () => fetchOrderLocalRow(orderLocalId!),
    enabled: !!orderLocalId,
    staleTime: 30 * 1000,
  });
  const seededWaiveOrderRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!orderWaiveQuery.data || seededWaiveOrderRef.current === orderLocalId) return;
    setWaiveFee(!!orderWaiveQuery.data.data?.waive_tech_fee);
    seededWaiveOrderRef.current = orderLocalId ?? null;
  }, [orderWaiveQuery.data, orderLocalId]);

  // Builds the DocumentDetails-shaped payload the SendDocumentDialog expects.
  // Uses the first invoice as the "primary" (drives default subject + auto-
  // selected attachment); the rest of the order's docs are available as
  // toggleable attachments.
  const sendPayload = React.useMemo<DocumentDetails | null>(() => {
    if (!orderId) return null;
    const docs = orderDocumentsQuery.data?.data ?? [];
    const primary = docs[0];
    const orderDocuments: NonNullable<DocumentDetails['orderDocuments']> = docs.map((d) => ({
      id: d.id,
      type: d.document_type,
      number: d.number ?? 'Draft invoice',
      amount: (d.grand_total_in_cents ?? 0) / 100,
      date: '',
      // Threaded to the Send dialog's draft-invoice guard (invoice-only).
      finalized: d.finalized,
    }));
    // Landfill receipts: one synthetic, individually-selectable attachment per
    // tonnage_receipt scan on the order, each keyed by its attachment id
    // (`landfill-receipt-att-<id>`) and labeled by the weighed line it came
    // from — same-named lines numbered ("…Pickup 1 / 2"), and a date stamp
    // appended when one line carries multiple receipts. `number` carries that
    // label; the dialog renders it as the receipt subtitle.
    if (orderLocalId) {
      const receiptLabels = buildReceiptLabels(receiptAttachments, orderLineItems);
      for (const r of receiptAttachments) {
        orderDocuments.push({
          id: `landfill-receipt-att-${r.id}`,
          type: 'landfill_receipt',
          number: receiptLabels.get(r.id) || r.line_label || r.name || 'Receipt',
          amount: 0,
          date: '',
        });
      }
    }
    // Purchase orders: one synthetic, individually-selectable attachment per
    // uploaded PO scan, keyed by its attachment id (`purchase-order-att-<id>`).
    // `number` carries the file name so the Send dialog renders it as the
    // subtitle. The backend streams the stored file as-is — no render.
    if (orderLocalId) {
      for (const po of purchaseOrderAttachments) {
        orderDocuments.push({
          id: `purchase-order-att-${po.id}`,
          type: 'purchase_order',
          number: po.name || 'Purchase order',
          amount: 0,
          date: '',
        });
      }
    }
    // Payment receipts: one selectable receipt PDF per recorded payment that has
    // a TWM ledger row (`transaction_id`). Keyed `receipt-txn-<txnPk>`; the
    // backend renders the branded receipt PDF for that transaction. `payments`
    // is sorted most-recent-first, so the first receipt entry is the latest —
    // the Send dialog pre-selects it when the receipt template is chosen. Voided
    // payments are skipped (no money collected → no receipt).
    for (const p of payments) {
      if (!p.transaction_id) continue;
      if ((p.status || '').toLowerCase() === 'voided') continue;
      const method = resolvePaymentMethod(p);
      const when = formatPaymentDate(p.paid_at);
      orderDocuments.push({
        id: `receipt-txn-${p.transaction_id}`,
        type: 'receipt',
        number: `${method}${when ? ` · ${when}` : ''}`,
        amount: (p.amount_in_cents ?? 0) / 100,
        date: '',
      });
    }
    return {
      id: primary?.id ?? '',
      number: primary?.number ?? '',
      type: primary?.document_type ?? 'invoice',
      status: '',
      exclTaxes: 0,
      inclTaxes: (primary?.grand_total_in_cents ?? 0) / 100,
      customerName: customerName ?? '',
      customerEmail: effectiveRecipientEmail,
      // Booqable order id — lets the Send-Email dialog fetch templates rendered
      // against this order's real values (instead of raw {token} placeholders).
      orderId: orderId ?? undefined,
      // Seed the Send dialog's tech-fee waive box from the same persisted state
      // the payment dialog toggles, so the two stay consistent.
      orderWaivesTechFee: waiveFee,
      orderDocuments,
    };
  }, [orderId, orderLocalId, receiptAttachments, purchaseOrderAttachments, orderLineItems, orderDocumentsQuery.data, payments, effectiveRecipientEmail, customerName, waiveFee]);

  // Order-level tags — persisted to Booqable's tag_list via
  // PATCH /booqable/orders/<id>. Seeded from the order's current tags below.
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const orderTagsQuery = useQuery<{ data?: { attributes?: { tag_list?: string[] | null } } }>({
    queryKey: ['order-tags', orderId],
    queryFn: async () => {
      const res = await apiFetch(
        `/booqable/orders/${encodeURIComponent(orderId!)}?fields[orders]=tag_list`,
      );
      if (!res.ok) throw new Error(`order-tags fetch failed: ${res.status}`);
      return res.json();
    },
    enabled: !!orderId,
    staleTime: 30 * 1000,
  });
  // Seed the chips from the server once per order. The ref guard stops a
  // post-save refetch (which re-runs this effect) from clobbering in-flight
  // edits — local state is already authoritative after the first seed.
  const seededTagsOrderRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!orderTagsQuery.data || seededTagsOrderRef.current === orderId) return;
    const serverTags = (orderTagsQuery.data.data?.attributes?.tag_list ?? []).filter(
      (t) => !ORDER_SYSTEM_TAGS.has(t),
    );
    setTags(serverTags);
    seededTagsOrderRef.current = orderId ?? null;
  }, [orderTagsQuery.data, orderId]);
  const [noteDraft, setNoteDraft] = useState('');
  // Drives Save-button visibility: shown while the textarea is focused
  // or while it holds unsaved text, so a user who tabs out without
  // saving still sees the affordance.
  const [notesFocused, setNotesFocused] = useState(false);
  const showNotesSave = notesFocused || noteDraft.trim().length > 0;
  // Internal notes — persisted to the polymorphic `notes` table via
  // /orders/<localId>/notes so the log survives a refresh. Keyed off the
  // TWM order uuid because notes attach to the local row (like tonnage and
  // attachments), which exists for any saved order whether promoted or not.
  const orderNotesQuery = useQuery<{
    data: {
      id: string;
      body: string;
      created_at: string | null;
      created_by_name?: string | null;
      employee_id?: string | null;
    }[];
  }>({
    queryKey: ['order-notes', orderLocalId],
    queryFn: async () => {
      const res = await apiFetch(`/orders/${encodeURIComponent(orderLocalId!)}/notes`);
      if (!res.ok) throw new Error(`order-notes fetch failed: ${res.status}`);
      return res.json();
    },
    enabled: !!orderLocalId,
    staleTime: 30 * 1000,
  });
  const savedNotes = orderNotesQuery.data?.data ?? [];

  const [savingNote, setSavingNote] = useState(false);
  const handleSaveNote = async () => {
    const text = noteDraft.trim();
    if (!text || !orderLocalId || savingNote) return;
    setSavingNote(true);
    try {
      const res = await apiFetch(`/orders/${encodeURIComponent(orderLocalId)}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: text }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error || `Save failed (HTTP ${res.status})`);
      }
      setNoteDraft('');
      queryClient.invalidateQueries({ queryKey: ['order-notes', orderLocalId] });
    } catch (err: any) {
      dispatchToast(
        <Toast>
          <ToastTitle>Couldn't save note</ToastTitle>
          <ToastBody>{err?.message || 'Please try again.'}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    } finally {
      setSavingNote(false);
    }
  };

  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const handleDeleteNote = async (noteId: string) => {
    if (!orderLocalId || deletingNoteId) return;
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    setDeletingNoteId(noteId);
    try {
      const res = await apiFetch(
        `/orders/${encodeURIComponent(orderLocalId)}/notes/${encodeURIComponent(noteId)}`,
        { method: 'DELETE' },
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error || `Delete failed (HTTP ${res.status})`);
      }
      queryClient.invalidateQueries({ queryKey: ['order-notes', orderLocalId] });
    } catch (err: any) {
      dispatchToast(
        <Toast>
          <ToastTitle>Couldn't delete note</ToastTitle>
          <ToastBody>{err?.message || 'Please try again.'}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    } finally {
      setDeletingNoteId(null);
    }
  };
  // Full-set replace on every add/remove. The backend normalizes the array
  // and re-applies the SANDBOX env marker, so we only ever send the
  // user-facing chips. Optimistic: the chip updates immediately and reverts
  // if the PATCH fails.
  const persistTags = async (next: string[], prev: string[]) => {
    if (!orderId) return;
    try {
      const res = await apiFetch(`/booqable/orders/${encodeURIComponent(orderId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag_list: next }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Save failed (HTTP ${res.status})`);
      }
      queryClient.invalidateQueries({ queryKey: ['order-tags', orderId] });
    } catch (err: any) {
      setTags(prev);
      dispatchToast(
        <Toast>
          <ToastTitle>Couldn't save tags</ToastTitle>
          <ToastBody>{err?.message || 'Please try again.'}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    }
  };
  const addTag = () => {
    const trimmed = newTag.trim();
    if (!trimmed || tags.includes(trimmed)) {
      setNewTag('');
      return;
    }
    const prev = tags;
    const next = [...tags, trimmed];
    setTags(next);
    setNewTag('');
    persistTags(next, prev);
  };
  const removeTag = (tag: string) => {
    const prev = tags;
    const next = tags.filter((t) => t !== tag);
    setTags(next);
    persistTags(next, prev);
  };

  // Sections are built inside the component so bodies that need
  // local state (tags, in particular) can reference it directly.
  const sections: DetailsSidebarSection[] = [
    {
      value: 'email',
      label: 'Email',
      body: (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
          <Button
            appearance="secondary"
            className={styles.sidebarButton}
            icon={<Mail20Regular />}
            onClick={() => setSendDialogOpen(true)}
            disabled={!sendPayload}
          >
            Send email
          </Button>
          <FluentLink
            disabled={!orderId}
            onClick={() => window.dispatchEvent(new CustomEvent('viewOrderEmailHistory'))}
            style={{ fontSize: '13px', cursor: orderId ? 'pointer' : 'not-allowed', alignSelf: 'flex-start' }}
          >
            View email history
          </FluentLink>
        </div>
      ),
    },
    {
      value: 'documents',
      label: 'Documents',
      body: (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
          <Tooltip
            content="Contracts are a planned feature — coming soon"
            relationship="label"
            withArrow
          >
            <span style={{ display: 'flex' }}>
              <Button
                appearance="secondary"
                className={styles.sidebarButton}
                icon={<DocumentAdd20Regular />}
                disabled
              >
                New contract
              </Button>
            </span>
          </Tooltip>
          <Button
            appearance="secondary"
            className={styles.sidebarButton}
            icon={creatingQuote ? <Spinner size="tiny" /> : <DocumentAdd20Regular />}
            onClick={handleNewQuote}
            disabled={!orderId || creatingQuote}
          >
            New quote
          </Button>
        </div>
      ),
    },
    {
      value: 'invoices',
      label: 'Invoices',
      extras:
        invoices.length > 0 ? (
          <Badge appearance="filled" color="informative" shape="circular">
            {invoices.length}
          </Badge>
        ) : null,
      body: orderDocumentsQuery.isLoading ? (
        <Spinner size="extra-small" />
      ) : invoices.length === 0 ? (
        <div className={styles.emptyBody}>No invoices yet.</div>
      ) : (
        <div className={styles.invoiceList}>
          {invoices.map((inv) => {
            const label = inv.finalized && inv.number ? `#${inv.number}` : 'Draft invoice';
            return (
              <button
                key={inv.id}
                type="button"
                className={styles.invoiceRow}
                onClick={() => openDocument(inv.id)}
              >
                <span className={styles.invoiceIconWrap}>
                  <Receipt20Regular />
                </span>
                <span className={styles.invoiceLabel}>{label}</span>
                <span className={styles.invoiceAmount}>
                  {formatMoney(inv.grand_total_in_cents)}
                </span>
              </button>
            );
          })}
        </div>
      ),
    },
    {
      value: 'payments',
      label: 'Payments',
      extras: (
        <>
          {paymentStatus === 'payment_due' && (
            <Tooltip content="Payment due" relationship="label" withArrow>
              <Alert20Filled className={styles.paymentDueIcon} />
            </Tooltip>
          )}
          {payments.length > 0 && (
            <Badge appearance="filled" color="informative" shape="circular">
              {payments.length}
            </Badge>
          )}
        </>
      ),
      body: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
          <div className={styles.paymentsTotalsBlock}>
            <div className={styles.paymentsTotalRow}>
              <span className={styles.paymentsTotalLabelMuted}>Paid</span>
              <span>
                {orderDocumentsQuery.isLoading
                  ? '—'
                  : formatMoney(
                      invoices.reduce((sum, inv) => sum + (inv.paid_in_cents ?? 0), 0)
                    )}
              </span>
            </div>
            <div className={styles.paymentsTotalDueRow}>
              <span>Due</span>
              <span>
                {orderDocumentsQuery.isLoading ? '—' : formatMoney(amountDueCents)}
              </span>
            </div>
          </div>
          {/* Per-payment list. Falls back to a muted "No payments yet"
              when the order has no payment_charges records. */}
          <div className={styles.paymentsDivider} />
          {orderPaymentsQuery.isLoading ? (
            <Spinner size="extra-small" />
          ) : payments.length === 0 ? (
            <div className={styles.paymentsEmpty}>No payments yet.</div>
          ) : (
            <div className={styles.paymentsList}>
              {payments.map((p) => {
                const method = resolvePaymentMethod(p);
                const date = formatPaymentDate(p.paid_at);
                const isPending = (p.status || '').toLowerCase() === 'pending';
                // A pending ACH is intentionally not in Booqable yet (posts at
                // settlement), so don't flag it as a sync failure.
                const unsynced = p.booqable_synced === false && !isPending;
                const isVoided = (p.status || '').toLowerCase() === 'voided';
                return (
                  <PaymentRowHoverCard key={p.id} payment={p} methodLabel={method} orderId={orderId}>
                    <div className={styles.paymentRow}>
                      <span className={styles.paymentDate}>{date || '—'}</span>
                      <span className={styles.paymentMethod} title={p.description || method}>
                        <PaymentMethodIcon method={method} className={styles.paymentMethodIcon} />
                        {method}
                      </span>
                      {isPending && (
                        <span
                          className={styles.pendingChip}
                          title="Bank transfer (ACH) submitted — funds settle in a few business days. The balance clears once it settles."
                        >
                          Pending settlement
                        </span>
                      )}
                      {unsynced && (
                        <span
                          className={styles.unsyncedChip}
                          title="This capture landed at Authorize.net but didn't sync to Booqable. Open the row to retry."
                        >
                          Not synced to Booqable
                        </span>
                      )}
                      <span
                        className={mergeClasses(
                          styles.paymentAmount,
                          isVoided && styles.paymentAmountVoided,
                        )}
                      >
                        {formatMoney(p.amount_in_cents)}
                      </span>
                    </div>
                  </PaymentRowHoverCard>
                );
              })}
            </div>
          )}
          <Button
            appearance="secondary"
            className={styles.sidebarButton}
            onClick={() => setPaymentDialogOpen(true)}
            disabled={orderDocumentsQuery.isLoading || amountDueCents <= 0}
          >
            New Payment
          </Button>
        </div>
      ),
    },
    {
      value: 'photos',
      label: 'Photos/Attachments',
      body: <PhotoSection orderId={orderLocalId ?? null} />,
    },
    {
      value: 'tags',
      label: 'Tags',
      body: !isPromoted ? (
        <div className={styles.emptyBody}>Available once the order is created.</div>
      ) : (
        <div
          className={sectionStyles.tagField}
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (target === e.currentTarget) {
              (target.querySelector('input') as HTMLInputElement | null)?.focus();
            }
          }}
        >
          {tags.map((tag, index) => (
            <span key={index} className={sectionStyles.tagChip}>
              {tag}
              <span
                className={sectionStyles.tagDismiss}
                role="button"
                aria-label={`Remove ${tag}`}
                onClick={() => removeTag(tag)}
              >
                ×
              </span>
            </span>
          ))}
          <Input
            size="large"
            className={styles.tagInput}
            value={newTag}
            onChange={(_, data) => setNewTag(data.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                addTag();
              } else if (e.key === 'Backspace' && newTag === '' && tags.length > 0) {
                e.preventDefault();
                removeTag(tags[tags.length - 1]);
              }
            }}
            onBlur={() => {
              if (newTag.trim()) addTag();
            }}
            placeholder={tags.length === 0 ? 'Add tags…' : ''}
          />
        </div>
      ),
    },
    {
      value: 'notes',
      label: 'Notes',
      body: !isPromoted ? (
        <div className={styles.emptyBody}>Available once the order is created.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Textarea
            value={noteDraft}
            onChange={(_, data) => setNoteDraft(data.value)}
            onFocus={() => setNotesFocused(true)}
            onBlur={() => setNotesFocused(false)}
            placeholder="Add notes…"
            resize="vertical"
            rows={4}
            style={{ width: '100%' }}
          />
          {showNotesSave && (
            <Button
              appearance="primary"
              // Prevent the textarea's blur from firing before the click
              // resolves — otherwise focus loss would hide the button mid-press.
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSaveNote}
              disabled={noteDraft.trim().length === 0 || !orderLocalId || savingNote}
              style={{ alignSelf: 'flex-end' }}
            >
              {savingNote ? 'Saving…' : 'Save'}
            </Button>
          )}
          {savedNotes.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
              {savedNotes.map((note) => {
                const created = note.created_at ? new Date(note.created_at) : null;
                return (
                  <div
                    key={note.id}
                    style={{
                      backgroundColor: tokens.colorNeutralBackground1,
                      borderRadius: '4px',
                      border: `1px solid ${tokens.colorNeutralStroke2}`,
                      padding: '8px 10px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                      <div style={{ flex: 1, fontSize: '13px', whiteSpace: 'pre-wrap' }}>{note.body}</div>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<Delete16Regular />}
                        aria-label="Delete note"
                        title="Delete note"
                        disabled={deletingNoteId === note.id}
                        onClick={() => handleDeleteNote(note.id)}
                      />
                    </div>
                    {(note.created_by_name || created) && (
                      <div style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
                        {[note.created_by_name, created ? formatBusinessDateTime(note.created_at) : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ),
    },
    {
      value: 'activity',
      label: 'Activity logs',
      body: (
        <div className={styles.emptyBody}>
          Activity logs aren't available yet — this is a planned feature.
        </div>
      ),
    },
  ];

  // Open the Payments section whenever there's an outstanding balance so
  // operators don't have to click to expand before acting. Uses the edge-
  // triggered variant so the section also opens reactively when the payment
  // status flips into a due state (e.g. a refund moves a Paid order back to
  // Partially Paid) — once open, the user can close it and the close sticks
  // until the next transition.
  const hasOutstandingBalance =
    paymentStatus === 'payment_due' || paymentStatus === 'partially_paid';
  const totalsCents = invoices.reduce(
    (acc, inv) => ({
      total: acc.total + (inv.grand_total_in_cents ?? 0),
      paid: acc.paid + (inv.paid_in_cents ?? 0),
    }),
    { total: 0, paid: 0 }
  );

  return (
    <>
      <DetailsSidebar
        sections={sections}
        width={300}
        resizable
        hideScrollbar
        storageKey="twm_order_sidebar_width"
        triggerOpenValues={[
          ...(hasOutstandingBalance ? ['payments'] : []),
        ]}
        mobileOverlay
        collapsed={collapsed}
        onClose={onClose}
        footer={
          isPhase ? (
            <Switch
              checked={!!allowProjectOverride}
              onChange={(_, d) => onAllowProjectOverrideChange?.(d.checked)}
              label="Override project settings"
            />
          ) : undefined
        }
      />
      <SendDocumentDialog
        isOpen={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        onUndo={() => setSendDialogOpen(true)}
        document={sendPayload}
      />
      <PaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        orderNumber={orderNumber ?? null}
        orderAmountInCents={totalsCents.total}
        orderPaidInCents={totalsCents.paid}
        orderWaivesTechFee={waiveFee}
        savedCards={savedCardsQuery.data?.data || []}
        customerName={customerName ?? null}
        onCharge={async (payload) => {
          if (!orderId) throw new Error('Missing order id');
          const amount = payload.orderCents + payload.depositCents;

          // AN merchant-receipt enrichment shared by every rail (card charge,
          // pre-auth, ACH) so all transaction records carry identical order
          // data: full billing address (AVS), phone, line items, tax. Backend
          // looks up customer ID + PO# server-side. billTo intentionally omits
          // email — AN's billTo XSD rejects it (E00003 "invalid child element
          // 'address'"); customer email rides the `customer` element, set
          // server-side from twm_customer_email.
          const trimmedName = (customerName ?? '').trim();
          const firstSpace = trimmedName.indexOf(' ');
          const firstName = firstSpace === -1 ? trimmedName : trimmedName.slice(0, firstSpace);
          const lastName = firstSpace === -1 ? '' : trimmedName.slice(firstSpace + 1);
          const countryISO =
            billingAddress?.country === 'United States' ? 'US'
            : billingAddress?.country === 'Canada' ? 'CA'
            : billingAddress?.country || '';
          const bill_to = billingAddress?.line1 ? {
            firstName: firstName || trimmedName,
            lastName,
            address: [billingAddress.line1, billingAddress.line2].filter(Boolean).join(' '),
            city: billingAddress.city || '',
            state: billingAddress.region || '',
            zip: billingAddress.postalCode || '',
            country: countryISO,
          } : undefined;
          const an_line_items = (orderLineItems || [])
            .filter((l) => l && l.name)
            .map((l) => ({
              id: l.id,
              name: l.name,
              quantity: l.quantity,
              unit_price_in_cents: l.priceInCents,
            }));

          if (payload.method === 'ach') {
            // ACH goes through the admin charge endpoint, which calls
            // Auth.Net + writes the NACHA audit row + records the Booqable
            // payment. Shares services/payments.charge_order_via_authnet
            // with the public booking flow so both rails behave identically.
            const res = await apiFetch(`/admin/orders/${encodeURIComponent(orderId)}/payments/charge`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                amount_in_cents: amount,
                payment_type: 'ach',
                payment_nonce: payload.achNonce,
                ach_authorization: payload.achAuthorization,
                bill_to,
                phone_number: customerPhone || undefined,
                line_items: an_line_items.length ? an_line_items : undefined,
                tax_in_cents: orderTaxCents || undefined,
                skip_tech_fee: payload.skipTechFee || undefined,
              }),
            });
            if (!res.ok) {
              const body = await res.json().catch(() => ({}));
              throw new Error(body?.error || `ACH charge failed (HTTP ${res.status})`);
            }
            queryClient.invalidateQueries({ queryKey: ['order-documents', orderId] });
            queryClient.invalidateQueries({ queryKey: ['order-payment-status', orderId] });
            // The Orders list pill reads payment_status off queryKeys.orders.all;
            // without this it stays stale (e.g. "Payment due" after a charge).
            queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
            dispatchToast(
              <Toast>
                <ToastTitle>{`Charged ${formatMoney(amount)} via bank transfer`}</ToastTitle>
              </Toast>,
              { intent: 'success' },
            );
            return;
          }

          if (payload.method === 'credit_card') {
            // Card charge / authorize: same /admin/orders/<id>/payments/charge
            // endpoint as ACH, branched server-side on payment_type. New
            // cards send a fresh Accept.js nonce + optional create_profile
            // flag; saved cards send a CIM payment_profile_id instead.
            // bill_to / line items / phone / tax are built above the method
            // branch and shared with ACH so every rail sends the same AN order
            // data (full billing address also lets AVS run — without it
            // established merchant accounts return reason 35).
            const res = await apiFetch(`/admin/orders/${encodeURIComponent(orderId)}/payments/charge`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                amount_in_cents: amount,
                payment_type: 'card',
                mode: payload.mode || 'charge',
                payment_nonce: payload.payment_nonce,
                saved_payment_profile_id: payload.saved_payment_profile_id,
                create_profile: payload.save_card === true,
                bill_to,
                phone_number: customerPhone || undefined,
                line_items: an_line_items.length ? an_line_items : undefined,
                tax_in_cents: orderTaxCents || undefined,
                skip_tech_fee: payload.skipTechFee || undefined,
              }),
            });
            if (!res.ok) {
              const body = await res.json().catch(() => ({}));
              throw new Error(body?.error || `Card charge failed (HTTP ${res.status})`);
            }
            const body = await res.json().catch(() => ({}));
            queryClient.invalidateQueries({ queryKey: ['order-documents', orderId] });
            queryClient.invalidateQueries({ queryKey: ['order-payment-status', orderId] });
            // Held auths only show on the page once /booqable/orders/<id>/payments
            // re-runs with the new authorized row merged in. Invalidate so the
            // operator sees the Authorized pill without a manual refresh.
            queryClient.invalidateQueries({ queryKey: ['order-payments', orderId] });
            // Keep the Orders list pill in sync (see note on the ACH branch).
            queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
            if (customerId) {
              // Refetch saved cards so a newly-saved chip appears on the
              // next dialog open without a page reload.
              queryClient.invalidateQueries({ queryKey: ['customer-saved-cards', customerId] });
            }
            const verb = payload.mode === 'authorize' ? 'Authorized' : 'Charged';
            dispatchToast(
              <Toast>
                <ToastTitle>{`${verb} ${formatMoney(amount)} on card`}</ToastTitle>
                {payload.mode === 'authorize' && (
                  <ToastBody>Capture or void from the order's Payments section.</ToastBody>
                )}
              </Toast>,
              { intent: 'success' },
            );
            // Surface the dedupe flag back to the dialog so it can swap the
            // "Card saved" confirmation for an info-style "card already saved"
            // banner instead of just silently reusing the existing profile.
            return {
              deduped: body?.deduped === true,
              payment_profile_id: body?.payment_profile_id || null,
            };
          }

          if (payload.method !== 'cash_other') return;
          const res = await apiFetch(`/booqable/orders/${encodeURIComponent(orderId)}/payments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              amount_in_cents: amount,
              payment_method: payload.cashSubMethod || 'cash',
              payment_date: payload.cashDate || undefined,
            }),
          });
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body?.error || `Payment failed (HTTP ${res.status})`);
          }
          queryClient.invalidateQueries({ queryKey: ['order-documents', orderId] });
          queryClient.invalidateQueries({ queryKey: ['order-payment-status', orderId] });
          queryClient.invalidateQueries({ queryKey: ['order-payments', orderId] });
          // Keep the Orders list pill in sync (see note on the ACH branch).
          queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
          dispatchToast(
            <Toast>
              <ToastTitle>{`Recorded ${formatMoney(amount)} (${payload.cashSubMethod || 'cash'})`}</ToastTitle>
            </Toast>,
            { intent: 'success' },
          );
        }}
      />
      <Toaster toasterId={toasterId} position="top" />
    </>
  );
};

export default React.memo(OrderRightSidebar);
